// src/components/gestionEvento/Ajustes/Competidores/Inscripciones/MemberTable.jsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Checkbox,
  Tooltip,
  Typography,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  TablePagination,
  Grid,
  InputBase,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Badge,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Search as SearchIcon,
  PublishedWithChanges as PublishedWithChangesIcon,
  FileDownload as FileDownloadIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { alpha } from '@mui/material/styles';

import { useParams } from 'react-router-dom';
import { useCompetencia } from '../../../../../context/CompetenciaContext';
import { useInscripciones } from '../../../../../context/InscripcionesContext';

import MemberRow from './MemberRow';
import AddAthleteModal from '../AddAthleteModal';
import ExportInscritosModal from '../ExportInscritosModal';
import AthleteSidebar from './AthleteSidebar';

const DRAWER_WIDTH = 420;

function MemberTable() {
  const { slug, competitionId } = useParams();
  const { competencias, setCompetencia, getCategorias, fetchPruebas } = useCompetencia();
  const {
    fetchInscripcionesCached,
    listenInscripcionesByCounter,
    updateInscripcion,
    deleteInscripcion,
    addInscripcion,
  } = useInscripciones();

  const competition = useMemo(
    () =>
      competencias.find((comp) => comp.id === competitionId) ||
      competencias.find((comp) => comp.slug === slug) ||
      {},
    [competencias, competitionId, slug]
  );

  // Carga real de categorías y pruebas (para edición)
  const [cats, setCats] = useState([]);
  const [tests, setTests] = useState([]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!competition?.id) return;
      try {
        const [categorias, pruebas] = await Promise.all([
          getCategorias?.(competition.id),
          fetchPruebas?.(competition.id),
        ]);
        if (!mounted) return;
        setCats(Array.isArray(categorias) ? categorias : []);
        setTests(Array.isArray(pruebas) ? pruebas : []);
      } catch {
        if (!mounted) return;
        setCats([]);
        setTests([]);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [competition?.id, getCategorias, fetchPruebas]);

  // Estado base de inscripciones
  const [members, setMembers] = useState([]);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // Buscador + debounce
  const [filterText, setFilterText] = useState('');
  const [localSearch, setLocalSearch] = useState('');
  const [filterField, setFilterField] = useState('all');
  const searchRef = useRef(null);

  // Paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Modales
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [openMassDeleteDialog, setOpenMassDeleteDialog] = useState(false);
  const [openMassDialog, setOpenMassDialog] = useState(false);
  const [massStatus, setMassStatus] = useState('');
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openExportModal, setOpenExportModal] = useState(false);

  // Sidebar
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);

  // Carga inicial + listener basado en contador
  useEffect(() => {
    let stop = () => {};
    (async () => {
      if (!competition?.id) return;
      const list = await fetchInscripcionesCached(competition.id);
      setMembers(list);
      stop = listenInscripcionesByCounter(competition.id);
    })();
    return () => stop?.();
  }, [competition?.id, fetchInscripcionesCached, listenInscripcionesByCounter]);

  // Debounce buscador
  useEffect(() => {
    setLocalSearch(filterText);
  }, []);
  useEffect(() => {
    const t = setTimeout(() => setFilterText(localSearch), 250);
    return () => clearTimeout(t);
  }, [localSearch]);

  // Atajo Ctrl/⌘ + K
  useEffect(() => {
    const onKey = (e) => {
      const isMac = navigator.platform.toUpperCase().includes('MAC');
      if (
        (isMac && e.metaKey && e.key.toLowerCase() === 'k') ||
        (!isMac && e.ctrlKey && e.key.toLowerCase() === 'k')
      ) {
        e.preventDefault();
        searchRef.current?.querySelector('input')?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Selección
  const handleSelectRow = (id, checked) => {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const filteredMembers = useMemo(() => {
    const t = (filterText || '').toLowerCase();
    const filterMember = (member) => {
      switch (filterField) {
        case 'codigoId':
          return member.codigoId?.toString().toLowerCase().includes(t);
        case 'nombre':
          return (`${member.nombre} ${member.apellido}`).toLowerCase().includes(t);
        case 'rama':
          return member.rama?.toLowerCase().includes(t);
        case 'categoria': {
          const catName =
            typeof member.categoria === 'object' ? member.categoria?.nombre : member.categoria;
          return (catName || '').toLowerCase().includes(t);
        }
        case 'equipo':
          return (typeof member.equipo === 'object' ? member.equipo.name : member.equipo)
            ?.toLowerCase()
            .includes(t);
        case 'prueba': {
          const pruebas = member.pruebasData ? member.pruebasData.map((p) => p.prueba).join(' ') : '';
          const relevos = member.relevosData ? member.relevosData.map((r) => r.prueba).join(' ') : '';
          return (pruebas + ' ' + relevos).toLowerCase().includes(t);
        }
        case 'status':
          return member.status?.toLowerCase().includes(t);
        case 'pago':
          return (member.paymentMethod || member.pago)?.toLowerCase().includes(t);
        default: {
          const pruebasStr = member.pruebasData ? member.pruebasData.map((p) => p.prueba).join(' ') : '';
          const relevosStr = member.relevosData ? member.relevosData.map((r) => r.prueba).join(' ') : '';
          const catName =
            typeof member.categoria === 'object' ? member.categoria?.nombre : member.categoria;
          const combined = `${member.codigoId} ${member.nombre} ${member.apellido} ${member.rama} ${catName} ${
            typeof member.equipo === 'object' ? member.equipo.name : member.equipo
          } ${member.status} ${member.pago} ${pruebasStr} ${relevosStr}`.toLowerCase();
          return combined.includes(t);
        }
      }
    };
    return (members || []).filter(filterMember);
  }, [members, filterText, filterField]);

  // Tabla paginada
  const paginatedMembers = useMemo(
    () => filteredMembers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredMembers, page, rowsPerPage]
  );

  const handleSelectAll = (e) => {
    const checked = e.target.checked;
    if (checked) {
      const allIds = filteredMembers.map((m) => m.id);
      setSelectedRows(new Set(allIds));
    } else {
      setSelectedRows(new Set());
    }
  };

  // Edit inline (persistiendo)
  const handleEdit = async (editedMember) => {
    await updateInscripcion(competition.id, editedMember.id, editedMember);
    setMembers((prev) => prev.map((m) => (m.id === editedMember.id ? editedMember : m)));
    setSnackbarOpen(true);
    setTimeout(() => setSnackbarOpen(false), 2000);
  };

  // Borrado
  const requestDelete = (row) => setDeleteCandidate(row);

  const confirmDelete = async () => {
    if (!deleteCandidate) return;
    await deleteInscripcion(competition.id, deleteCandidate.id);
    setMembers((prev) => prev.filter((m) => m.id !== deleteCandidate.id));
    setDeleteCandidate(null);
    setSnackbarOpen(true);
    setTimeout(() => setSnackbarOpen(false), 2000);
  };

  const requestMassDelete = () => setOpenMassDeleteDialog(true);

  const handleDeleteMass = async () => {
    const ids = Array.from(selectedRows);
    await Promise.all(ids.map((id) => deleteInscripcion(competition.id, id)));
    setMembers((prev) => prev.filter((m) => !selectedRows.has(m.id)));
    setSelectedRows(new Set());
    setOpenMassDeleteDialog(false);
    setSnackbarOpen(true);
    setTimeout(() => setSnackbarOpen(false), 2000);
  };

  // Estatus masivo
  const handleMassStatusChange = async (newStatus) => {
    if (!newStatus) return;
    const ids = Array.from(selectedRows);
    // Persistimos
    await Promise.all(ids.map((id) => updateInscripcion(competition.id, id, { status: newStatus })));
    // Reflejamos UI
    setMembers((prev) => prev.map((m) => (selectedRows.has(m.id) ? { ...m, status: newStatus } : m)));
    setMassStatus(newStatus);
    setOpenMassDialog(false);
    setSnackbarOpen(true);
    setTimeout(() => setSnackbarOpen(false), 2000);
  };

  // Agregar / Exportar
  const handleAddAthlete = async (newAthlete) => {
    const res = await addInscripcion(competition.id, newAthlete);
    if (res?.wasAdded) {
      setMembers((prev) => [...prev, { ...newAthlete, id: res.id }]);
      setSnackbarOpen(true);
      setTimeout(() => setSnackbarOpen(false), 2000);
    }
  };

  // Sidebar
  const openDetails = (member) => {
    setSelectedMember(member);
    setDetailsOpen(true);
  };
  const saveFromSidebar = async (updatedMember) => {
    await updateInscripcion(competition.id, updatedMember.id, updatedMember);
    setMembers((prev) => prev.map((m) => (m.id === updatedMember.id ? updatedMember : m)));
    setSelectedMember(updatedMember);
    setSnackbarOpen(true);
  };

  // UI
  const numSelected = selectedRows.size;
  const rowCount = filteredMembers.length;

  return (
    <Box sx={{ display: 'flex', width: '100%' }}>
      {/* LEFT: tabla + acciones */}
      <Box
        sx={{
          flexGrow: 1,
          transition: (t) => t.transitions.create(['margin', 'width']),
          width: detailsOpen ? { md: `calc(100% - ${DRAWER_WIDTH}px)` } : '100%',
          mr: detailsOpen ? { md: `${DRAWER_WIDTH}px` } : 0,
        }}
      >
        {/* Botones Agregar/Exportar */}
        <Box sx={{ maxWidth: 1200, m: 'auto', mt: 2, mb: 1, px: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpenAddModal(true)}
                sx={(t) => ({
                  borderRadius: 999,
                  textTransform: 'none',
                  fontWeight: 700,
                  bgcolor: alpha(t.palette.primary.main, 0.08),
                  color: t.palette.primary.main,
                  border: `1px solid ${alpha(t.palette.primary.main, 0.25)}`,
                  boxShadow: `0 2px 8px ${alpha(t.palette.primary.main, 0.18)}`,
                  '&:hover': { bgcolor: alpha(t.palette.primary.main, 0.12) },
                })}
              >
                Agregar Atleta
              </Button>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<FileDownloadIcon />}
                onClick={() => setOpenExportModal(true)}
                sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 700, px: 2 }}
              >
                Exportar
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Header sticky: buscador + filtros + acciones */}
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: (t) => t.zIndex.appBar,
            px: 2,
            pt: 2,
            pb: 1.5,
            mb: 1.25,
            bgcolor: 'background.default',
            backdropFilter: 'blur(6px)',
            borderBottom: (t) => `1px solid ${t.palette.divider}`,
            borderRadius: 3,
          }}
        >
          <Grid container spacing={1.5} alignItems="center">
            {/* Buscador */}
            <Grid item xs={12} md={6} lg={6}>
              <Box
                ref={searchRef}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  borderRadius: 999,
                  px: 1,
                  py: 0.5,
                  bgcolor: (t) => alpha(t.palette.background.paper, 0.8),
                  border: (t) => `1px solid ${alpha(t.palette.text.primary, 0.08)}`,
                  boxShadow: (t) => `0 2px 10px ${alpha(t.palette.common.black, 0.06)}`,
                  transition: 'box-shadow .2s, transform .05s',
                  '&:focus-within': {
                    boxShadow: (t) => `0 6px 22px ${alpha(t.palette.primary.main, 0.18)}`,
                  },
                }}
              >
                <SearchIcon sx={{ mx: 1, opacity: 0.7 }} />
                <InputBase
                  value={localSearch}
                  onChange={(e) => setLocalSearch(e.target.value)}
                  placeholder="Buscar… (Ctrl/⌘ + K)"
                  inputProps={{ 'aria-label': 'buscar inscritos' }}
                  sx={{ flex: 1, fontSize: { xs: '0.85rem', sm: '0.9rem' }, py: 0.5 }}
                />
                {!!localSearch && (
                  <IconButton
                    size="small"
                    onClick={() => setLocalSearch('')}
                    aria-label="limpiar búsqueda"
                    sx={{ mr: 0.5 }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Grid>

            {/* Filtro por campo */}
            <Grid item xs={12} md={3} lg={3}>
              <FormControl fullWidth size="small">
                <InputLabel id="filter-field-label">Filtrar por</InputLabel>
                <Select
                  labelId="filter-field-label"
                  label="Filtrar por"
                  value={filterField}
                  onChange={(e) => setFilterField(e.target.value)}
                >
                  <MenuItem value="all">Todos</MenuItem>
                  <MenuItem value="codigoId"># Registro</MenuItem>
                  <MenuItem value="nombre">Nombre</MenuItem>
                  <MenuItem value="prueba">Prueba</MenuItem>
                  <MenuItem value="rama">Rama</MenuItem>
                  <MenuItem value="categoria">Categoría</MenuItem>
                  <MenuItem value="equipo">Equipo</MenuItem>
                  <MenuItem value="status">Status</MenuItem>
                  <MenuItem value="pago">Pago</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Acciones si hay selección */}
            <Grid
              item
              xs={12}
              md={3}
              lg={3}
              sx={{
                display: 'flex',
                justifyContent: { xs: 'flex-start', md: 'flex-end' },
                gap: 1,
                mt: { xs: 1, md: 0 },
                flexWrap: 'wrap',
              }}
            >
              {numSelected > 0 && (
                <>
                  <Badge color="primary" badgeContent={numSelected} overlap="circular">
                    <Tooltip title="Editar Estatus Masivo">
                      <Button
                        onClick={() => setOpenMassDialog(true)}
                        startIcon={<PublishedWithChangesIcon />}
                        sx={(t) => ({
                          borderRadius: 999,
                          px: { xs: 1.25, sm: 1.75 },
                          py: 1,
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: { xs: '0.8rem', sm: '0.875rem' },
                          bgcolor: alpha(t.palette.primary.main, 0.08),
                          color: t.palette.primary.main,
                          border: `1px solid ${alpha(t.palette.primary.main, 0.24)}`,
                          '&:hover': { bgcolor: alpha(t.palette.primary.main, 0.12) },
                        })}
                      >
                        Estatus
                      </Button>
                    </Tooltip>
                  </Badge>

                  <Badge color="error" badgeContent={numSelected} overlap="circular">
                    <Tooltip title="Eliminar Seleccionados">
                      <Button
                        onClick={() => setOpenMassDeleteDialog(true)}
                        startIcon={<DeleteIcon />}
                        sx={(t) => ({
                          borderRadius: 999,
                          px: { xs: 1.25, sm: 1.75 },
                          py: 1,
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: { xs: '0.8rem', sm: '0.875rem' },
                          bgcolor: alpha(t.palette.error.main, 0.08),
                          color: t.palette.error.main,
                          border: `1px solid ${alpha(t.palette.error.main, 0.24)}`,
                          '&:hover': { bgcolor: alpha(t.palette.error.main, 0.12) },
                        })}
                      >
                        Eliminar
                      </Button>
                    </Tooltip>
                  </Badge>
                </>
              )}
            </Grid>
          </Grid>
        </Box>

        {/* Tabla principal */}
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: 3,
            boxShadow: 6,
            overflowX: 'auto',
            overflowY: 'auto',
            maxHeight: { xs: '150vh', md: '150vh' },
          }}
        >
          <Table
            stickyHeader
            aria-label="Tabla de inscripciones"
            sx={{
              minWidth: 900,
              tableLayout: 'fixed',
              '& th, & td': {
                fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' },
                py: { xs: 1, sm: 1.15, md: 1.3 },
                px: { xs: 1, sm: 1.25, md: 1.5 },
                lineHeight: 1.3,
              },
              '& thead th': {
                bgcolor: 'background.paper',
                fontWeight: 600,
                letterSpacing: 0.2,
                borderBottom: (t) => `1px solid ${t.palette.divider}`,
                fontSize: { xs: '0.78rem', sm: '0.85rem', md: '0.95rem' },
              },
              '& tbody tr:hover': { bgcolor: 'action.hover' },
              '& tbody tr:nth-of-type(odd)': { bgcolor: 'action.selectedOpacity' },
              '& .MuiIconButton-root': { padding: { xs: 0.25, sm: 0.5, md: 0.75 } },
              '& td.MuiTableCell-paddingCheckbox': { width: { xs: 40, sm: 44, md: 48 } },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    color="primary"
                    indeterminate={numSelected > 0 && numSelected < rowCount}
                    checked={rowCount > 0 && numSelected === rowCount}
                    onChange={handleSelectAll}
                    inputProps={{ 'aria-label': 'seleccionar todos' }}
                    disabled={rowCount === 0}
                  />
                </TableCell>
                <TableCell><strong># Registro</strong></TableCell>
                <TableCell><strong>Nombre</strong></TableCell>
                <TableCell><strong>Rama</strong></TableCell>
                <TableCell><strong>Categoría</strong></TableCell>
                <TableCell><strong>Equipo</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Pago</strong></TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {paginatedMembers.length > 0 ? (
                paginatedMembers.map((member) => (
                  <MemberRow
                    key={member.id}
                    row={member}
                    selected={selectedRows.has(member.id)}
                    onSelectChange={handleSelectRow}
                    onEdit={handleEdit}
                    onDelete={requestDelete}
                    categorias={cats}
                    equipos={competition.equipos || []}
                    competition={competition}
                    updateCompetencia={setCompetencia}
                    openDetails={() => openDetails(member)}
                  />
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <Typography>
                      {filterText
                        ? `No se encontraron atletas con “${filterText}”.`
                        : 'No hay inscripciones registradas.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Filas por página"
            component="div"
            count={filteredMembers.length}
            page={page}
            onPageChange={(_e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </TableContainer>

        {/* Diálogos */}
        <Dialog open={Boolean(deleteCandidate)} onClose={() => setDeleteCandidate(null)}>
          <DialogTitle>Confirmar Eliminación</DialogTitle>
          <DialogContent>
            <DialogContentText>
              ¿Estás seguro de que deseas eliminar a{' '}
              {deleteCandidate ? `${deleteCandidate.nombre} ${deleteCandidate.apellido}` : ''}? Esta acción es irreversible.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteCandidate(null)}>Cancelar</Button>
            <Button onClick={confirmDelete} color="error" variant="contained">
              Eliminar
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={openMassDeleteDialog} onClose={() => setOpenMassDeleteDialog(false)}>
          <DialogTitle>Confirmar Eliminación Masiva</DialogTitle>
          <DialogContent>
            <DialogContentText>
              ¿Estás seguro de que deseas eliminar {Array.from(selectedRows).length} atletas? Esta acción es irreversible y no podrás
              recuperar la información.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenMassDeleteDialog(false)}>Cancelar</Button>
            <Button onClick={handleDeleteMass} color="error" variant="contained">
              Eliminar
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={openMassDialog} onClose={() => setOpenMassDialog(false)}>
          <DialogTitle>Editar Estatus Masivo</DialogTitle>
          <DialogContent>
            <DialogContentText>Seleccione el nuevo Estatus para los atletas seleccionados:</DialogContentText>
            <Select fullWidth value={massStatus} onChange={(e) => setMassStatus(e.target.value)} sx={{ mt: 1 }}>
              <MenuItem value="Pagado">Pagado</MenuItem>
              <MenuItem value="EnProgreso">En Progreso</MenuItem>
              <MenuItem value="Pendiente">Pendiente</MenuItem>
              <MenuItem value="Rechazado">Rechazado</MenuItem>
            </Select>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenMassDialog(false)}>Cancelar</Button>
            <Button onClick={() => handleMassStatusChange(massStatus)} color="primary" variant="contained">
              Guardar
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar y modales */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={2000}
          onClose={() => setSnackbarOpen(false)}
          message="Acción realizada"
        />

        <AddAthleteModal
          open={openAddModal}
          onClose={() => setOpenAddModal(false)}
          onAdd={handleAddAthlete}
        />

        <ExportInscritosModal
          open={openExportModal}
          onClose={() => setOpenExportModal(false)}
          inscripciones={members}
          competitionName={competition?.general?.nombre || competition?.nombre || ''}
        />
      </Box>

      {/* RIGHT: Sidebar persistente */}
      <AthleteSidebar
        open={detailsOpen}
        width={DRAWER_WIDTH}
        member={selectedMember}
        categorias={cats}
        pruebas={tests}
        competition={competition}
        equipos={competition.equipos || []}
        onClose={() => setDetailsOpen(false)}
        onSave={saveFromSidebar}
        setCompetencia={setCompetencia}
      />
    </Box>
  );
}

MemberTable.propTypes = { members: PropTypes.array };
export default MemberTable;
